/**
 * setInterval wrapper that pauses while the tab is hidden.
 *
 * Why: background polling (notifications, job status, etc.) runs 24/7 per open
 * tab. A tab left open overnight produces thousands of wasted requests. Gating
 * on document.visibilityState cuts that to zero while hidden, with no change
 * in behavior when visible. See work item #5436.
 */
export class VisibilityAwareInterval {
  private timer: ReturnType<typeof setInterval> | null = null;
  private running = false;
  private readonly listener = () => this.sync();

  constructor(
    private readonly callback: () => void,
    private readonly intervalMs: number,
  ) {}

  start() {
    if (this.running) {
      return;
    }
    this.running = true;
    document.addEventListener('visibilitychange', this.listener);
    this.sync();
  }

  stop() {
    if (!this.running) {
      return;
    }
    this.running = false;
    document.removeEventListener('visibilitychange', this.listener);
    this.clearTimer();
  }

  private sync() {
    if (!this.running) {
      return;
    }
    if (document.visibilityState === 'hidden') {
      this.clearTimer();
    } else if (!this.timer) {
      this.timer = setInterval(this.callback, this.intervalMs);
    }
  }

  private clearTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}
